import { faEye, faEyeSlash, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { formatValueToLocale } from "../../../utils/method";
import "./text-field.scss";

// React requires controlled inputs to always have a defined value. Coerce
// null/undefined to an empty string while preserving valid falsy values such
// as 0 - `value || ""` would incorrectly blank those out.
export const toInputValue = (value) => value ?? "";

// True when a value is present (not null, undefined, or an empty string).
const isFilled = (value) =>
	value !== null && value !== undefined && value !== "";

// Custom hook for flexible decimal formatting logic (robust for German locale)
const useDecimalFormatting = (value, decimalPlaces, focused) => {
	const [displayValue, setDisplayValue] = useState(value);

	useEffect(() => {
		if (typeof decimalPlaces === "number" && !focused && isFilled(value)) {
			let numValue = value;
			if (typeof value === "string") {
				// Find the last separator (dot or comma)
				const lastDot = value.lastIndexOf('.');
				const lastComma = value.lastIndexOf(',');
				let normalized = value;
				if (lastDot > lastComma) {
					// Dot is the decimal separator
					// Remove all commas (thousands), keep last dot as decimal
					normalized = value.replace(/,/g, '');
				} else if (lastComma > lastDot) {
					// Comma is the decimal separator
					// Remove all dots (thousands), replace last comma with dot
					const parts = value.split(',');
					const decimalPart = parts.pop();
					normalized = parts.join('').replace(/\./g, '') + '.' + decimalPart;
				} else {
					// No separator or ambiguous, just remove thousands
					normalized = value.replace(/,/g, '').replace(/\./g, '');
				}
				numValue = parseFloat(normalized);
			}
			if (!isNaN(numValue)) {
				setDisplayValue(formatValueToLocale(numValue, false, decimalPlaces, decimalPlaces));
			} else {
				setDisplayValue(value);
			}
		} else {
			setDisplayValue(value);
		}
	}, [value, focused, decimalPlaces]);

	return [displayValue, setDisplayValue];
};

const TextFieldAtom = forwardRef((props, ref) => {
	const {
		id,
		label,
		type = "text",
		className,
		placeholder,
		value = "",
		onChange,
		error = "",
		required = false,
		errorTextPlaceholder = {},
		hint,
		hintClassName = "text-muted",
		disabled = false,
		decimalPlaces,
		onBlur,
		onFocus,
		suffix,
		showClearIcon = false,
		onClear,
		clearIconTitle = "Clear",
		size,
		...rest
	} = props;

	const { t } = useTranslation();
	const [focused, setFocused] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [displayValue, setDisplayValue] = useDecimalFormatting(
		value,
		decimalPlaces,
		focused,
	);

	// Generate unique IDs for accessibility
	const fieldId = useMemo(
		() => id || `text-field-${Math.random().toString(36).substr(2, 9)}`,
		[id],
	);
	const hintId = useMemo(() => `${fieldId}-hint`, [fieldId]);

	// Event handlers
	const handleKeyDown = useCallback(
		(e) => {
			// If decimalPlaces is set, block non-numeric keys except for allowed ones
			if (typeof decimalPlaces === "number") {
				// Allow: digits, comma, dot, minus, backspace, delete, arrows, tab, enter
				const allowedKeys = [
					"Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter",
					",", ".", "-"
				];
				if (
					!/\d/.test(e.key) &&
					!allowedKeys.includes(e.key)
				) {
					e.preventDefault();
				}
			} else if (type === "number" && e.key === "-" && rest.min !== undefined && rest.min >= 0) {
				e.preventDefault();
			}
		},
		[type, decimalPlaces, rest.min],
	);

	const handleWheel = useCallback(
		(e) => {
			if (type === "number") {
				e.target.blur();
			}
		},
		[type],
	);

	const handleInputChange = useCallback(
		(e) => {
			// For file inputs, pass the original event unchanged
			if (type === "file") {
				setDisplayValue(e.target.value);
				onChange?.(e);
				return;
			}

			let inputValue = e.target.value;
			let transformed = false;

			// If decimalPlaces is set, force only numeric input (allow comma/dot for decimals)
			if (typeof decimalPlaces === "number") {
				// Allow only digits, one comma or dot, and optional leading minus (if needed)
				inputValue = inputValue
					.replace(/[^\d.,-]/g, "") // Only digits, comma, dot, minus
					.replace(/(,|\.){2,}/g, "$1") // Only one comma or dot
					.replace(/(?!^[-]?\d*)([-])/g, ""); // Only one leading minus if needed

				// Prevent multiple decimals (comma or dot)
				const parts = inputValue.split(/,|\./);
				if (parts.length > 2) {
					inputValue = parts[0] + (inputValue.includes(',') ? ',' : '.') + parts[1];
				}
				transformed = true;
			} else if (type === "number" && inputValue < 0 && rest.min !== undefined && rest.min >= 0) {
				inputValue = "";
				transformed = true;
			}

			setDisplayValue(inputValue);

			// Plain text fields need no value rewriting - forward the native event
			// untouched so consumers get the real target (name/id intact). Only the
			// numeric-cleaning paths above need a synthetic event, and even then we
			// must carry name/id over: { ...e.target } drops them because DOM element
			// properties live on the prototype, not as own-enumerable props.
			if (!transformed) {
				onChange?.(e);
				return;
			}

			const syntheticEvent = {
				...e,
				target: {
					...e.target,
					name: e.target.name,
					id: e.target.id,
					value: inputValue,
				},
			};
			onChange?.(syntheticEvent);
		},
		[type, decimalPlaces, onChange, setDisplayValue, rest.min],
	);

	const handleFocus = useCallback(
		(e) => {
			setFocused(true);
			// On focus, show raw value for editing
			if (typeof decimalPlaces === "number" && isFilled(value)) {
				setDisplayValue(value.toString());
			}
			onFocus?.(e);
		},
		[decimalPlaces, value, onFocus, setDisplayValue],
	);

	const handleBlur = useCallback(
		(e) => {
			setFocused(false);
			const targetValue = e.target.value;

			if (typeof decimalPlaces === "number" && isFilled(targetValue)) {
				// Accept comma as decimal separator for German locale
				// Remove all dots (thousand separators), replace comma with dot for parsing
				let normalizedValue = targetValue.replace(/\./g, '').replace(',', '.');
				let numericValue = parseFloat(normalizedValue);

				// Only format if it's a valid number
				if (!isNaN(numericValue)) {
					// Always display with comma as decimal separator
					const formattedValue = formatValueToLocale(numericValue, false, decimalPlaces, decimalPlaces);
					setDisplayValue(formattedValue);
				} else {
					setDisplayValue(targetValue);
				}
			}

			onBlur?.(e);
		},
		[decimalPlaces, onBlur, setDisplayValue],
	);

	// Toggle password visibility
	const togglePasswordVisibility = useCallback(() => {
		setShowPassword((prev) => !prev);
	}, []);

	// Determine the actual input type
	const inputType = useMemo(() => {
		if (type === "password") {
			return showPassword ? "text" : "password";
		}
		// If decimalPlaces is set, always use text to allow formatting
		if (typeof decimalPlaces === "number") {
			return "text";
		}
		return type;
	}, [type, showPassword, decimalPlaces]);

	// Determine if we should show the eye icon
	const showEyeIcon = type === "password";

	// Determine if we should show the clear icon
	const shouldShowClearIcon = showClearIcon && value && value.length > 0;

	// Calculate padding for icons - only needed when clear icon overlays inside the input
	// (i.e. no suffix; with suffix the InputGroup handles layout via flex)
	const rightPadding = useMemo(() => {
		let padding = 12; // Base padding
		if (shouldShowClearIcon && !suffix) padding += 30;
		return padding;
	}, [shouldShowClearIcon, suffix]);

	// Handle clear action
	const handleClear = useCallback(() => {
		if (onClear) {
			onClear();
		} else if (onChange) {
			// Create a synthetic event for onChange
			const syntheticEvent = {
				target: { value: "" },
				currentTarget: { value: "" }
			};
			setDisplayValue("");
			onChange(syntheticEvent);
		}
	}, [onClear, onChange, setDisplayValue]);

	return (
		<div className="text-field-wrapper">
			{label && (
				<Form.Label htmlFor={fieldId}>
					{label}
					{required && (
						<span className="text-danger">
							*
						</span>
					)}
				</Form.Label>
			)}

			<Form.Group className="position-relative">
				<InputGroup
					size={size}
					className={error ? "tf-input-group--invalid" : undefined}
				>
					<Form.Control
						id={fieldId}
						type={inputType}
						className={className}
						placeholder={placeholder}
						value={toInputValue(displayValue)}
						onChange={handleInputChange}
						onFocus={handleFocus}
						onBlur={handleBlur}
						onKeyDown={handleKeyDown}
						onWheel={handleWheel}
						disabled={disabled}
						required={required}
						isInvalid={!!error}
						ref={ref}
						style={{
							paddingRight: `${rightPadding}px`,
							...rest.style
						}}
						{...rest}
					/>

					{shouldShowClearIcon && suffix && (
						<InputGroup.Text
							onClick={handleClear}
							title={clearIconTitle}
							role="button"
							tabIndex={0}
							className="tf-clear-btn"
						>
							<FontAwesomeIcon icon={faTimes} />
						</InputGroup.Text>
					)}

					{showEyeIcon && (
						<Button
							variant="outline-secondary"
							onClick={togglePasswordVisibility}
							disabled={disabled}
						>
							<FontAwesomeIcon
								icon={showPassword ? faEyeSlash : faEye}
								className="tf-eye-icon"
							/>
						</Button>
					)}

					{suffix && <InputGroup.Text>{suffix}</InputGroup.Text>}
				</InputGroup>

				{/* Clear icon - rendered outside InputGroup so it doesn't participate in flex layout */}
				{shouldShowClearIcon && !suffix && (
					<FontAwesomeIcon
						icon={faTimes}
						onClick={handleClear}
						title={clearIconTitle}
						className="tf-clear-icon"
						style={{ right: "12px" }}
					/>
				)}
			</Form.Group>

			{error && (
				<div className="tf-error-text">
					{t(error, errorTextPlaceholder)}
				</div>
			)}

			{hint && !error && (
				<div
					id={hintId}
					className={`${hintClassName} small mt-1 tf-hint`}
				>
					{hint}
				</div>
			)}
		</div>
	);
});

TextFieldAtom.displayName = "TextFieldAtom";

export default React.memo(TextFieldAtom);
